import { useCallback } from 'react';
import { flushSync } from 'react-dom';
import {
  findInteractionBarPart,
  stripEphemeralToolStepsFromTrace,
} from '../chat-ui/uiActionLabel';

/** Shared human-gate helpers for Chat and widget. */
export function useAgentUserGate({
  setMessages,
  isAwaitingUserFromDone,
}) {
  const dismissGate = useCallback(() => {
    setMessages((prev) => {
      const next = [...prev];
      const i = next.length - 1;
      if (i >= 0 && next[i].role === 'assistant') {
        next[i] = {
          ...next[i],
          awaitingUser: false,
          ui_parts: (next[i].ui_parts || []).filter(
            (p) => (p.component || '') !== 'user_gate_bar',
          ),
          pipeline_trace: stripEphemeralToolStepsFromTrace(next[i].pipeline_trace),
        };
      }
      return next;
    });
  }, [setMessages]);

  const markResuming = useCallback(() => {
    flushSync(() => {
      setMessages((prev) => {
        const next = [...prev];
        const i = next.length - 1;
        if (i >= 0 && next[i].role === 'assistant') {
          next[i] = {
            ...next[i],
            streaming: true,
            awaitingUser: false,
            pipeline_trace: stripEphemeralToolStepsFromTrace(next[i].pipeline_trace),
          };
        }
        return next;
      });
    });
  }, [setMessages]);

  const applyDoneGateFlags = useCallback((msg, done) => {
    if (!done?.awaiting_user) return msg;
    return {
      ...msg,
      awaitingUser: true,
      gateKind: done?.gate_kind || null,
    };
  }, []);

  const isResumeAction = useCallback((action) => {
    const { actionId, resumePendingTool } = action;
    return (
      resumePendingTool
      || actionId === 'write.confirm'
      || actionId === 'write.always_allow'
      || actionId === 'oauth.continue'
    );
  }, []);

  const isGateCancelAction = useCallback((actionId) => (
    actionId === 'write.deny'
    || actionId === 'write.cancel'
    || actionId === 'oauth.cancel'
  ), []);

  const findGatePart = useCallback((msg, isLast) => {
    if (!isLast) return null;
    if (msg.awaitingUser) {
      return findInteractionBarPart(msg.ui_parts);
    }
    return null;
  }, []);

  return {
    dismissGate,
    markResuming,
    applyDoneGateFlags,
    isResumeAction,
    isGateCancelAction,
    findGatePart,
    isAwaitingUserFromDone,
  };
}

export function buildResumePayload(action, { priorMessages, agentRequestFields, agentSessionId }) {
  return {
    message: '',
    messages: priorMessages,
    ...agentRequestFields(),
    ui_action_id: action.actionId,
    ui_action_payload: action.payload,
    ui_part_id: action.partId,
    resume_pending_tool: true,
    always_allow_write: Boolean(action.alwaysAllowWrite || action.actionId === 'write.always_allow'),
    agent_session_id: agentSessionId,
  };
}
